import { parseLeadKey, toPlatform, type Platform } from '../lib/platform';

/**
 * Reads the suggested-searches and outcome responses defensively (SCALE
 * contract, V2). The portal can go live before the Discovery service does:
 * today's server answers `{ items, countsBySource }` with no category, yield
 * or evidence, and has no outcomes endpoint at all. Every field therefore has
 * a stated fallback here, and nothing is invented — a number the server
 * didn't send is `null` and is hidden, not shown as 0.
 */

type Raw = Record<string, unknown>;

const isObj = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
/** Numbers also arrive as strings: Postgres `numeric` columns serialise that way. */
const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};
const first = (o: Raw, ...keys: string[]): unknown => {
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

// ── Categories ──────────────────────────────────────────────────────────────

export const SUGGESTION_CATEGORIES = ['campaign', 'focus_niche', 'proven_creators', 'top_folder', 'trending', 'lookalike'] as const;
export type KnownCategory = (typeof SUGGESTION_CATEGORIES)[number];
/** `other` = a category this build doesn't know yet; shown under All only. */
export type SuggestionCategory = KnownCategory | 'other';

export const CATEGORY_LABELS: Record<SuggestionCategory, string> = {
  campaign: 'Campaigns',
  focus_niche: 'Focus niches',
  proven_creators: 'Proven creators',
  top_folder: 'Top folders',
  trending: 'Trending',
  lookalike: 'Lookalike',
  other: 'Other',
};

/** Singular, for the pill on a card. */
export const CATEGORY_PILL: Record<SuggestionCategory, string> = {
  campaign: 'Campaign',
  focus_niche: 'Focus niche',
  proven_creators: 'Proven creators',
  top_folder: 'Top folder',
  trending: 'Trending',
  lookalike: 'Lookalike',
  other: 'Suggested',
};

/** What an empty tab says, so "nothing here" also says when something will be. */
export const CATEGORY_EMPTY: Record<SuggestionCategory | 'all', { title: string; hint: string }> = {
  all: { title: 'Nothing waiting', hint: 'Generate now to look for new terms.' },
  campaign: {
    title: 'No campaign suggestions',
    hint: 'These come from live campaigns: the official sound and the tags named in each brief.',
  },
  focus_niche: {
    title: 'No focus-niche suggestions',
    hint: 'These come from the tags our creators in each focus niche already use. Mark niches as focused to get more.',
  },
  proven_creators: {
    title: 'No proven creators yet',
    hint: 'These appear once creators from Discovery join and post.',
  },
  top_folder: {
    title: 'No top folders yet',
    hint: 'These appear once a folder’s leads have been messaged and some of them post.',
  },
  trending: {
    title: 'Nothing trending yet',
    hint: 'These appear when a term keeps turning up in recent runs that led to posts.',
  },
  lookalike: {
    title: 'Nothing sent from Lookalike',
    hint: 'Sounds sent from a folder’s Find lookalikes analysis wait here for review.',
  },
  other: { title: 'Nothing here', hint: 'Generate now to look for new terms.' },
};

export function toCategory(v: unknown): SuggestionCategory {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  if ((SUGGESTION_CATEGORIES as readonly string[]).includes(s)) return s as KnownCategory;
  // Spellings a server might reasonably send for the same five.
  if (s === 'campaigns') return 'campaign';
  if (s === 'niche' || s === 'focus_niches' || s === 'focus-niche') return 'focus_niche';
  if (s === 'proven_creator' || s === 'proven') return 'proven_creators';
  if (s === 'top_folders' || s === 'folder') return 'top_folder';
  return 'other';
}

// ── Suggestions ─────────────────────────────────────────────────────────────

export type SuggestionStatus = 'pending' | 'approved' | 'rejected' | 'expired';

/** Something a suggestion is based on: a creator (linkable) or a folder. */
export interface EvidenceRef {
  kind: 'creator' | 'folder';
  label: string;
  /** Creators only. */
  platform: Platform | null;
  /** Folders only, when the server sends the id. */
  folderPublicId: string | null;
}

export interface SuggestionEvidence {
  basedOn: EvidenceRef[];
  sampleSize: number | null;
  postsPer100: number | null;
  /** The server's own sentence, when it sends one. */
  why: string | null;
  /** Pre-V2 field: 'campaign sound' | 'campaign brief' | … */
  origin: string | null;
}

export interface SuggestedSearch {
  publicId: string;
  term: string;
  searchMode: 'hashtag' | 'sound';
  /** The sound a sound suggestion searches (string: TikTok ids exceed safe integers). */
  musicId: string | null;
  platform: Platform;
  score: number | null;
  reach: number | null;
  seedCount: number;
  /** Reach band: niche | topic | broad. */
  band: string | null;
  topicTerms: string[];
  source: string;
  sourceLabel: string;
  status: SuggestionStatus;
  category: SuggestionCategory;
  /** Posts per 100 leads predicted from the source's history. Null = not predicted. */
  expectedYield: number | null;
  priority: number | null;
  evidence: SuggestionEvidence;
  createdAt: string | null;
}

function evidenceRef(raw: unknown, category: SuggestionCategory, platform: Platform): EvidenceRef | null {
  const folderish = category === 'top_folder';
  if (typeof raw === 'string') {
    const label = raw.trim();
    if (!label) return null;
    if (folderish && !label.startsWith('@')) return { kind: 'folder', label, platform: null, folderPublicId: null };
    // "instagram:handle" names its own platform; a bare handle is on the suggestion's.
    const keyed = /^(instagram|x|tiktok):/.test(label) ? parseLeadKey(label) : null;
    return {
      kind: 'creator',
      label: (keyed?.username ?? label).replace(/^@/, ''),
      platform: keyed?.platform ?? platform,
      folderPublicId: null,
    };
  }
  if (!isObj(raw)) return null;
  const handle = str(first(raw, 'handle', 'username'));
  if (handle) {
    return {
      kind: 'creator',
      label: handle.replace(/^@/, ''),
      platform: raw.platform || raw.channel ? toPlatform(first(raw, 'platform', 'channel')) : platform,
      folderPublicId: null,
    };
  }
  const name = str(first(raw, 'name', 'folderName', 'label'));
  const folderPublicId = str(first(raw, 'folderPublicId', 'publicId'));
  if (!name && !folderPublicId) return null;
  return { kind: 'folder', label: name ?? 'Folder', platform: null, folderPublicId };
}

function normaliseEvidence(raw: unknown, category: SuggestionCategory, platform: Platform): SuggestionEvidence {
  const o = isObj(raw) ? raw : {};
  const basedOn = (Array.isArray(o.basedOn) ? o.basedOn : [])
    .map((r) => evidenceRef(r, category, platform))
    .filter((r): r is EvidenceRef => r !== null);
  return {
    basedOn,
    sampleSize: num(o.sampleSize),
    postsPer100: num(o.postsPer100),
    why: str(o.why),
    origin: str(o.origin),
  };
}

export function normaliseSuggestion(raw: unknown): SuggestedSearch | null {
  if (!isObj(raw)) return null;
  const publicId = str(raw.publicId);
  const term = str(raw.term);
  if (!publicId || !term) return null;
  const source = str(raw.source) ?? '';
  // Until the server sends a category, the source is the category.
  const category = toCategory(raw.category ?? source);
  const platform = toPlatform(raw.platform);
  const status = str(raw.status);
  return {
    publicId,
    term,
    searchMode: raw.searchMode === 'sound' ? 'sound' : 'hashtag',
    musicId: str(first(raw, 'musicId', 'music_id')) ?? (typeof raw.musicId === 'number' ? String(raw.musicId) : null),
    platform,
    score: num(raw.score),
    reach: num(raw.reach),
    seedCount: num(raw.seedCount) ?? 0,
    band: str(raw.band),
    topicTerms: Array.isArray(raw.topicTerms) ? raw.topicTerms.filter((t): t is string => typeof t === 'string') : [],
    source,
    sourceLabel: str(raw.sourceLabel) ?? '',
    status: status === 'approved' || status === 'rejected' || status === 'expired' ? status : 'pending',
    category,
    expectedYield: num(first(raw, 'expectedYield', 'expected_yield')),
    priority: num(raw.priority),
    evidence: normaliseEvidence(raw.evidence, category, platform),
    createdAt: str(raw.createdAt),
  };
}

export const SUGGESTION_SORTS = ['expectedYield', 'score', 'reach', 'newest'] as const;
export type SuggestionSort = (typeof SUGGESTION_SORTS)[number];
export const SORT_LABELS: Record<SuggestionSort, string> = {
  expectedYield: 'Expected yield',
  score: 'Score',
  reach: 'Reach',
  newest: 'Newest',
};
export const toSort = (v: unknown): SuggestionSort =>
  (SUGGESTION_SORTS as readonly unknown[]).includes(v) ? (v as SuggestionSort) : 'expectedYield';

export interface SuggestionQuery {
  /** '' = all categories. */
  category: string;
  status: string;
  sort: SuggestionSort;
  dir: 'asc' | 'desc';
  /** '' = any platform. */
  platform: string;
  q: string;
  page: number;
}

export type CategoryCounts = Record<'all' | KnownCategory, number | null>;

export interface SuggestionList {
  rows: SuggestedSearch[];
  counts: CategoryCounts;
  /** Suggestions waiting, across categories. Null = not reported. */
  pending: number | null;
  page: number;
  /** Null = the server didn't page (everything is in `rows`). */
  pageSize: number | null;
  total: number;
  /** False for a pre-V2 server: filtering, sorting and counts were done here. */
  serverSide: boolean;
  /** Whether any row carries yield or evidence, i.e. outcome feedback has reached Discovery. */
  hasFeedback: boolean;
}

const sortValue = (s: SuggestedSearch, sort: SuggestionSort): number | null =>
  sort === 'expectedYield'
    ? s.expectedYield
    : sort === 'score'
      ? s.score
      : sort === 'reach'
        ? s.reach
        : s.createdAt
          ? Date.parse(s.createdAt)
          : null;

/** Unknown values last in either direction; ties fall back to score, then term. */
export function sortSuggestions(rows: SuggestedSearch[], sort: SuggestionSort, dir: 'asc' | 'desc'): SuggestedSearch[] {
  const sign = dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = sortValue(a, sort);
    const bv = sortValue(b, sort);
    if (av == null || Number.isNaN(av)) {
      if (!(bv == null || Number.isNaN(bv))) return 1;
    } else if (bv == null || Number.isNaN(bv)) {
      return -1;
    } else if (av !== bv) {
      return (av - bv) * sign;
    }
    return (b.score ?? 0) - (a.score ?? 0) || a.term.localeCompare(b.term);
  });
}

function matchesQuery(s: SuggestedSearch, q: string): boolean {
  const needle = q.trim().toLowerCase().replace(/^#/, '');
  if (!needle) return true;
  return [s.term, s.sourceLabel, ...s.topicTerms].some((t) => t.toLowerCase().includes(needle));
}

function countByCategory(rows: SuggestedSearch[]): CategoryCounts {
  const counts = { all: rows.length } as CategoryCounts;
  for (const c of SUGGESTION_CATEGORIES) counts[c] = rows.filter((r) => r.category === c).length;
  return counts;
}

function serverCounts(raw: unknown): CategoryCounts | null {
  if (!isObj(raw)) return null;
  const by = isObj(raw.byCategory) ? raw.byCategory : null;
  if (!by) return null;
  const counts = {} as CategoryCounts;
  let sum = 0;
  for (const c of SUGGESTION_CATEGORIES) {
    // A category the server left out has nothing in it.
    counts[c] = num(by[c]) ?? 0;
    sum += counts[c] ?? 0;
  }
  // Categories this build doesn't know still count towards All.
  const extra = Object.entries(by)
    .filter(([k]) => !(SUGGESTION_CATEGORIES as readonly string[]).includes(k) && k !== 'all')
    .reduce((n, [, v]) => n + (num(v) ?? 0), 0);
  counts.all = num(by.all) ?? num(raw.total) ?? sum + extra;
  return counts;
}

/**
 * One page of suggestions from either server generation.
 *
 * V2 answers `{ rows, counts:{ byCategory, pending } }` already filtered and
 * sorted. The older endpoint answers `{ items, countsBySource }` for a status
 * and ignores everything else in the query, so for that shape the category,
 * platform and text filters, the sort and the tab counts are applied here.
 */
export function normaliseSuggestionList(raw: unknown, query: SuggestionQuery): SuggestionList {
  const o = isObj(raw) ? raw : {};
  const list = Array.isArray(raw) ? raw : Array.isArray(o.rows) ? o.rows : Array.isArray(o.items) ? o.items : [];
  const all = list.map(normaliseSuggestion).filter((s): s is SuggestedSearch => s !== null);
  const counts = serverCounts(o.counts);
  const serverSide = Array.isArray(o.rows) && counts !== null;
  const hasFeedback = all.some((s) => s.expectedYield != null || s.evidence.postsPer100 != null || s.evidence.basedOn.length > 0);

  if (serverSide) {
    return {
      rows: all,
      counts: counts!,
      pending: isObj(o.counts) ? num(o.counts.pending) : null,
      page: num(o.page) ?? query.page,
      pageSize: num(first(o, 'pageSize', 'limit')),
      total: num(o.total) ?? all.length,
      serverSide: true,
      hasFeedback,
    };
  }

  const searched = all.filter(
    (s) => matchesQuery(s, query.q) && (!query.platform || s.platform === query.platform),
  );
  const inCategory = query.category ? searched.filter((s) => s.category === query.category) : searched;
  const rows = sortSuggestions(inCategory, query.sort, query.dir);
  return {
    rows,
    counts: counts ?? countByCategory(searched),
    pending: query.status === 'pending' ? all.length : null,
    page: 1,
    pageSize: null,
    total: rows.length,
    serverSide: false,
    hasFeedback,
  };
}

// ── Wording ─────────────────────────────────────────────────────────────────

/** "6.2" / "12" — one decimal below 10, none above. */
export function formatRate(n: number): string {
  if (n >= 10) return String(Math.round(n));
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

export const per100Label = (n: number): string => `${formatRate(n)} ${n === 1 ? 'post' : 'posts'} per 100 leads`;

export function formatReach(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * Why a term is suggested, in one sentence. The server's own `why` wins; the
 * rest is built from what the evidence holds, so an older row still explains
 * itself ("3 of our creators in this niche use it").
 */
export function whyLine(s: SuggestedSearch): string {
  const e = s.evidence;
  const n = e.sampleSize ?? (e.basedOn.length > 0 ? e.basedOn.length : null);
  const rate = e.postsPer100 ?? s.expectedYield;
  const where = s.sourceLabel ? ` in ${s.sourceLabel}` : '';
  let base: string;

  if (e.why) base = e.why.replace(/[.\s]+$/, '');
  else if (s.category === 'proven_creators') {
    base = n != null ? `Based on ${plural(n, 'creator')} who joined or posted${where}` : `Based on creators who joined or posted${where}`;
  } else if (s.category === 'top_folder') {
    const folder = e.basedOn.find((b) => b.kind === 'folder')?.label ?? s.sourceLabel;
    base = folder ? `More like the folder “${folder}”` : 'More like one of your best folders';
    if (n != null && e.sampleSize != null) base += `, from ${plural(n, 'lead')}`;
  } else if (s.category === 'trending') {
    base = n != null ? `Rising across ${plural(n, 'recent run')} that led to posts` : 'Rising in recent runs that led to posts';
  } else if (e.origin === 'campaign sound') base = 'The campaign’s official audio';
  else if (e.origin === 'campaign brief') base = 'Named in the brief · confirmed a real tag on TikTok';
  else if (e.origin === 'lookalike sound') base = s.seedCount > 0 ? `Used by ${plural(s.seedCount, 'creator')}${where}` : `Sent from a Lookalike analysis${where}`;
  else if (s.seedCount > 0) base = `${s.seedCount} of our creators in this niche use it`;
  else base = e.origin ?? (s.sourceLabel ? `Suggested for ${s.sourceLabel}` : 'Suggested');

  // The server's sentence may already quote the rate ("9 in 100", "per 100 leads").
  if (rate != null && !/\b100\b/.test(base)) base += ` — ${per100Label(rate)}`;
  return base;
}

export type Confidence = 'high' | 'medium' | 'low';

/** How much history a predicted yield rests on. Null when there is no sample to judge by. */
export function confidenceOf(s: SuggestedSearch): Confidence | null {
  const n = s.evidence.sampleSize ?? (s.evidence.basedOn.length > 0 ? s.evidence.basedOn.length : null);
  if (n == null || (s.expectedYield == null && s.evidence.postsPer100 == null)) return null;
  return n >= 20 ? 'high' : n >= 8 ? 'medium' : 'low';
}

export const CONFIDENCE_LABELS: Record<Confidence, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

export const BAND_LABELS: Record<string, string> = { niche: 'Niche tag', topic: 'Topic tag', broad: 'Broad tag' };

// ── Bulk approve / reject ───────────────────────────────────────────────────

export interface BulkResult {
  done: string[];
  failed: { id: string; message: string }[];
}

/** A bulk endpoint's answer, whatever it lists; ids it doesn't mention are taken as done. */
export function normaliseBulkResult(raw: unknown, ids: string[]): BulkResult {
  const o = isObj(raw) ? raw : {};
  const failed: BulkResult['failed'] = [];
  for (const f of Array.isArray(o.failed) ? o.failed : Array.isArray(o.errors) ? o.errors : []) {
    if (typeof f === 'string') failed.push({ id: f, message: 'Failed' });
    else if (isObj(f)) {
      const id = str(first(f, 'id', 'publicId'));
      if (id) failed.push({ id, message: str(first(f, 'error', 'message', 'reason')) ?? 'Failed' });
    }
  }
  const bad = new Set(failed.map((f) => f.id));
  return { done: ids.filter((id) => !bad.has(id)), failed };
}

// ── Outcomes (GET /api/insights/outcomes) ───────────────────────────────────

export interface Outcome {
  leads: number | null;
  messaged: number | null;
  replied: number | null;
  joined: number | null;
  posted: number | null;
  irrelevant: number | null;
  postsPer100: number | null;
}

export interface Outcomes {
  /** False when the server has no outcomes endpoint yet. */
  available: boolean;
  byFolder: Map<string, Outcome>;
  bySchedule: Map<string, Outcome>;
  qualityByFolder: Map<string, SearchQualityNotice>;
}

export const NO_OUTCOMES: Outcomes = {
  available: false,
  byFolder: new Map(),
  bySchedule: new Map(),
  qualityByFolder: new Map(),
};

export function normaliseOutcome(raw: unknown): Outcome | null {
  if (!isObj(raw)) return null;
  const out: Outcome = {
    leads: num(first(raw, 'leads', 'leadsAdded', 'added', 'exported')),
    messaged: num(raw.messaged),
    replied: num(raw.replied),
    joined: num(raw.joined),
    posted: num(raw.posted),
    irrelevant: num(first(raw, 'irrelevant', 'flaggedIrrelevant', 'irrelevantFlags')),
    postsPer100: num(raw.postsPer100),
  };
  return Object.values(out).some((v) => v != null) ? out : null;
}

/** Whether there is anything worth a chip: no outreach yet reads as silence, not "0 posted". */
export function hasOutcome(o: Outcome | null | undefined): o is Outcome {
  return !!o && ((o.posted ?? 0) > 0 || (o.joined ?? 0) > 0 || (o.messaged ?? 0) > 0 || (o.replied ?? 0) > 0 || o.postsPer100 != null);
}

function outcomeRows(o: Raw, ...keys: string[]): unknown[] {
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
    // Keyed by id instead of listed.
    if (isObj(v)) return Object.entries(v).map(([id, row]) => (isObj(row) ? { publicId: id, ...row } : row));
  }
  return [];
}

export function normaliseOutcomes(raw: unknown): Outcomes {
  const o = isObj(raw) ? raw : {};
  const byFolder = new Map<string, Outcome>();
  const bySchedule = new Map<string, Outcome>();
  const qualityByFolder = new Map<string, SearchQualityNotice>();

  for (const r of outcomeRows(o, 'folders', 'byFolder')) {
    if (!isObj(r)) continue;
    const id = str(first(r, 'folderPublicId', 'publicId'));
    if (!id) continue;
    const outcome = normaliseOutcome(r);
    if (outcome) byFolder.set(id, outcome);
    const notice = folderQualityNotice(r);
    if (notice) qualityByFolder.set(id, notice);
  }
  for (const r of outcomeRows(o, 'schedules', 'bySchedule')) {
    if (!isObj(r)) continue;
    const id = str(first(r, 'schedulePublicId', 'publicId'));
    const outcome = normaliseOutcome(r);
    if (id && outcome) bySchedule.set(id, outcome);
  }
  // Notices listed on their own, each naming its folder.
  for (const r of Array.isArray(o.notices) ? o.notices : []) {
    if (!isObj(r)) continue;
    const id = str(first(r, 'folderPublicId', 'folderId'));
    const notice = normaliseQualityNotice(r);
    if (id && notice) qualityByFolder.set(id, notice);
  }
  return { available: true, byFolder, bySchedule, qualityByFolder };
}

// ── The search_quality notice ───────────────────────────────────────────────

export interface SearchQualityNotice {
  /** 0–100. Null = not reported. */
  irrelevantPct: number | null;
  flagged: number | null;
  reviewed: number | null;
  /** The most common notes outreachers left, most frequent first. */
  notes: { note: string; count: number | null }[];
  /** The server's suggested fix, in words. */
  suggestedFix: string | null;
  /** Words the fix proposes excluding, when the server lists them. */
  excludeTerms: string[];
  /** A minimum topic match the fix proposes, 0–1. */
  minTopicMatch: number | null;
  message: string | null;
}

const NOTICE_KINDS = ['search_quality', 'searchQuality', 'search-quality'];

export function normaliseQualityNotice(raw: unknown): SearchQualityNotice | null {
  if (!isObj(raw)) return null;
  const kind = first(raw, 'kind', 'type', 'code');
  if (kind !== undefined && !NOTICE_KINDS.includes(String(kind))) return null;
  // Some servers nest the numbers under `data` / `details`.
  const d: Raw = { ...(isObj(raw.data) ? raw.data : {}), ...(isObj(raw.details) ? raw.details : {}), ...raw };

  const notes: SearchQualityNotice['notes'] = [];
  for (const n of Array.isArray(first(d, 'notes', 'commonNotes', 'topNotes')) ? (first(d, 'notes', 'commonNotes', 'topNotes') as unknown[]) : []) {
    if (typeof n === 'string' && n.trim()) notes.push({ note: n.trim(), count: null });
    else if (isObj(n)) {
      const note = str(first(n, 'note', 'text', 'label'));
      if (note) notes.push({ note, count: num(first(n, 'count', 'n')) });
    }
  }

  const fixRaw = first(d, 'suggestedFix', 'suggestion', 'fix');
  const fix = isObj(fixRaw) ? fixRaw : {};
  const terms = first(fix, 'excludeTerms', 'excludedTerms') ?? first(d, 'excludeTerms', 'excludedTerms');
  const pctRaw = num(first(d, 'irrelevantPct', 'flaggedPct', 'pct', 'irrelevantShare'));
  const flagged = num(first(d, 'flagged', 'flaggedIrrelevant', 'irrelevant'));
  const reviewed = num(first(d, 'reviewed', 'reviewedLeads'));
  // A share sent as 0–1 reads as a percentage; so does one derived from the counts.
  const pct =
    pctRaw != null
      ? pctRaw <= 1
        ? pctRaw * 100
        : pctRaw
      : flagged != null && reviewed
        ? (flagged / reviewed) * 100
        : null;

  const notice: SearchQualityNotice = {
    irrelevantPct: pct != null ? Math.round(pct) : null,
    flagged,
    reviewed,
    notes,
    suggestedFix: typeof fixRaw === 'string' ? str(fixRaw) : str(first(fix, 'message', 'text', 'summary')),
    excludeTerms: Array.isArray(terms) ? terms.filter((t): t is string => typeof t === 'string' && !!t.trim()) : [],
    minTopicMatch: num(first(fix, 'minTopicMatch') ?? first(d, 'minTopicMatch')),
    message: str(first(d, 'message', 'summary')),
  };
  const empty =
    notice.irrelevantPct == null && notice.notes.length === 0 && !notice.suggestedFix && !notice.message && notice.excludeTerms.length === 0;
  return empty ? null : notice;
}

/** The notice on a folder row (or an outcomes row for a folder), wherever the server put it. */
export function folderQualityNotice(folder: unknown): SearchQualityNotice | null {
  if (!isObj(folder)) return null;
  for (const n of Array.isArray(folder.notices) ? folder.notices : []) {
    if (isObj(n) && NOTICE_KINDS.includes(String(first(n, 'kind', 'type', 'code')))) {
      const notice = normaliseQualityNotice(n);
      if (notice) return notice;
    }
  }
  const direct = first(folder, 'searchQuality', 'search_quality', 'searchQualityNotice');
  return isObj(direct) ? normaliseQualityNotice(direct) : null;
}

export function qualityHeadline(n: SearchQualityNotice): string {
  if (n.irrelevantPct != null && n.flagged != null && n.reviewed != null) {
    return `${n.irrelevantPct}% of the leads reviewed in this folder were flagged irrelevant (${n.flagged.toLocaleString()} of ${n.reviewed.toLocaleString()}).`;
  }
  if (n.irrelevantPct != null) return `${n.irrelevantPct}% of the leads reviewed in this folder were flagged irrelevant.`;
  return n.message ?? 'Outreachers keep flagging this folder’s leads as irrelevant.';
}

/** The fix in words: the server's sentence, or one built from the parts it sent. */
export function qualityFix(n: SearchQualityNotice): string | null {
  if (n.suggestedFix) return n.suggestedFix;
  const parts: string[] = [];
  if (n.minTopicMatch != null) parts.push(`raise the minimum topic match to ${n.minTopicMatch}`);
  if (n.excludeTerms.length > 0) parts.push(`exclude ${n.excludeTerms.map((t) => `“${t}”`).join(', ')}`);
  if (parts.length === 0) return null;
  const s = parts.join(' and ');
  return `${s.charAt(0).toUpperCase()}${s.slice(1)} on this folder’s next search.`;
}
